import { useState } from 'react';

import {
  WEEK_DAYS,
  monthDays,
  monthTitle,
  pickDay,
  rangeLabel,
  shiftMonth,
} from '../../lib/audit';
import type { AuditRange, CalendarDay } from '../../lib/audit';

const CALENDAR_ICON = 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4';
const CHEVRON_DOWN = 'M6 9l6 6 6-6';

/** O mês a que uma chave de dia pertence; sem ela, o mês corrente. */
function monthOf(key: string): Date {
  if (!key) return new Date();

  const [year, month] = key.split('-').map(Number);

  return new Date(year, month - 1, 1);
}

interface DateRangePickerProps {
  range: AuditRange;
  onChange: (range: AuditRange) => void;
}

/**
 * Fundo de um dia da grade.
 *
 * As bordas do intervalo são escuras e o miolo recebe o fundo claro *sem
 * arredondamento*, porque é a sequência de células coladas que desenha a faixa
 * — arredondar cada uma quebraria a faixa em bolinhas soltas.
 */
function dayStyle(day: CalendarDay, hasEnd: boolean): string {
  if (day.isEdge) {
    const corners = !hasEnd
      ? 'rounded-lg'
      : day.isStart
        ? 'rounded-l-lg'
        : 'rounded-r-lg';

    return `bg-navy-800 text-white ${corners}`;
  }

  if (day.inRange) return 'bg-brand-100/40 text-navy-800';

  return `rounded-lg bg-white ${day.isToday ? 'ring-navy-800 ring-1 ring-inset' : ''}`;
}

/**
 * O campo de período do filtro: um botão que abre calendário e horários.
 *
 * O calendário é próprio, e não um `<input type="date">` do navegador, porque o
 * filtro é de intervalo com hora — dois campos nativos lado a lado deixariam de
 * mostrar a faixa selecionada, que é a informação que a pessoa procura ao
 * conferir o que escolheu.
 */
export function DateRangePicker({ range, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => monthOf(range.start));

  /**
   * O calendário sempre abre no mês do início escolhido.
   *
   * Sincronizar na abertura, e não só ao montar, é o que faz um atalho como
   * "últimos 30 dias" — clicado com o calendário fechado — levar a grade junto:
   * sem isso ela abriria em outro mês, sem nenhuma marca à vista.
   */
  const toggle = () => {
    const next = !open;
    if (next) setMonth(monthOf(range.start));
    setOpen(next);
  };

  const days = monthDays(month, range);
  const hasEnd = Boolean(range.end && range.end !== range.start);

  const time = (name: 'fromTime' | 'toTime', label: string) => (
    <label className="text-ink-400 flex flex-1 flex-col gap-1 text-[10.5px] font-extrabold">
      {label}
      <input
        type="time"
        value={range[name]}
        onChange={(event) => onChange({ ...range, [name]: event.target.value })}
        className="border-line-300 focus:border-navy-800 rounded-[9px] border px-2.5 py-2 text-[13px] font-semibold outline-none focus:ring-2 focus:ring-[#00244733]"
      />
    </label>
  );

  return (
    <div className="text-ink-600 relative flex min-w-0 flex-col gap-1.5 text-[11.5px] font-bold desktop:col-span-2">
      Período
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="border-line-300 hover:border-navy-800 text-ink-900 flex items-center gap-2.5 rounded-[10px] border bg-white px-3 py-2.5 text-left text-[13.5px] font-semibold"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#002447"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={CALENDAR_ICON} />
        </svg>

        <span className="min-w-0 flex-1 truncate">{rangeLabel(range)}</span>

        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#93A0B8"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={CHEVRON_DOWN} />
        </svg>
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />

          <div className="animate-rise-fast border-line-200 shadow-popover absolute top-[calc(100%+8px)] left-0 z-40 w-[min(330px,calc(100vw-40px))] rounded-[14px] border bg-white p-3.5">
            <div className="mb-2.5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMonth(shiftMonth(month, -1))}
                className="border-line-200 text-navy-800 size-7 rounded-lg border bg-white font-extrabold"
              >
                ‹<span className="sr-only">Mês anterior</span>
              </button>

              <div className="text-ink-900 flex-1 text-center text-[13px] font-extrabold first-letter:uppercase">
                {monthTitle(month)}
              </div>

              <button
                type="button"
                onClick={() => setMonth(shiftMonth(month, 1))}
                className="border-line-200 text-navy-800 size-7 rounded-lg border bg-white font-extrabold"
              >
                ›<span className="sr-only">Próximo mês</span>
              </button>
            </div>

            <div className="mb-1 grid grid-cols-7 gap-0.5">
              {WEEK_DAYS.map((weekDay, position) => (
                <div
                  key={position}
                  className="text-ink-400 py-0.5 text-center text-[10.5px] font-extrabold"
                >
                  {weekDay}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-y-0.5">
              {days.map((day) =>
                day.blank ? (
                  <div key={day.key} className="h-8" />
                ) : (
                  <button
                    key={day.key}
                    type="button"
                    onClick={() => onChange(pickDay(range, day.key))}
                    className={`text-ink-900 flex h-8 items-center justify-center text-[12.5px] font-bold ${dayStyle(
                      day,
                      hasEnd,
                    )}`}
                  >
                    {day.label}
                  </button>
                ),
              )}
            </div>

            <div className="mt-3 flex gap-2">
              {time('fromTime', 'HORA INÍCIO')}
              {time('toTime', 'HORA FIM')}
            </div>

            <div className="border-surface-200 mt-3 flex items-center gap-2 border-t pt-3">
              <button
                type="button"
                onClick={() => onChange({ ...range, start: '', end: '' })}
                className="text-ink-600 text-[12px] font-extrabold"
              >
                Limpar
              </button>

              <div className="flex-1" />

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="bg-navy-800 hover:bg-navy-950 rounded-[9px] px-5 py-2.5 text-[12.5px] font-extrabold text-white"
              >
                Aplicar
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
